// Search titles and snippets for artwork pages. Used by build-static.ts for
// the static HTML and by the app after it loads, so both say the same thing.
import { firstYear, styleDisplayName } from "./hubs";

export interface SeoArtwork {
  id?: string | number;
  title?: string;
  artist?: string;
  date?: string;
  style?: string;
  genre?: string;
  made_of?: string[];
  description?: string;
}

const SITE = "Ariadne's Gallery";
const TITLE_MAX = 60;
const DESCRIPTION_MAX = 155;

// Works whose usual English name differs from our record's title, from
// Search Console queries: people search "the stone breakers", we say
// "The Stone Breaker".
const SEARCHED_TITLES: Record<string, string> = {
  "12129": "The Stone Breakers",
  "49284": "Communion of the Dying",
};

// What kind of work it is, from its materials and genre, for titles like
// "The Stone Breakers, 1849 painting by Gustave Courbet". Many searches add
// the word: "communion of the dying painting", "stone breakers painting".
const PAINT = /\b(oil|tempera|acrylic|gouache|fresco|watercolou?r|canvas|panel)\b/i;
const DRAW = /\b(ink|pen|pencil|chalk|charcoal|graphite|crayon)\b/i;
export function artworkKind(a: SeoArtwork): string | undefined {
  const made = (a.made_of ?? []).join(" ");
  const genre = (a.genre ?? "").toLowerCase();
  if (/sketch|study/.test(genre)) return "study";
  if (/illustration/.test(genre)) return "illustration";
  if (PAINT.test(made)) return "painting";
  if (/\bpastel\b/i.test(made)) return "pastel";
  if (DRAW.test(made)) return "drawing";
  if (/painting|portrait|landscape|still life|marina|cityscape/.test(genre)) return "painting";
  return undefined;
}

// Google cuts titles at about 60 characters. Say what kind of work it is and
// when it was made, as in "The Stone Breakers, 1849 painting by Gustave
// Courbet", and add the site name only when it still fits.
export function artworkSeoTitle(a: SeoArtwork): string {
  const year = firstYear(a.date);
  const name = (SEARCHED_TITLES[String(a.id)] ?? a.title ?? "").replace(/(?<!\.\.)\.$/, "");
  // "Study of Hands, 1506 study" says it twice.
  const kind = [artworkKind(a)].find((k) => k && !name.toLowerCase().includes(k));
  const core = kind && year
    ? `${name}, ${year} ${kind} by ${a.artist}`
    : `${name} by ${a.artist}${year ? ` (${year})` : ""}`;
  const withSite = `${core} | ${SITE}`;
  return withSite.length <= TITLE_MAX ? withSite : core;
}

export function plainText(htmlText: string): string {
  return htmlText
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

// Split into sentences, but not after initials or abbreviations ("Miguel N. Lira", "St. Jerome").
function sentences(text: string): string[] {
  const out: string[] = [];
  for (const piece of text.split(/(?<=[.!?]["”’]?)\s+(?=["“A-Z])/)) {
    const prev = out[out.length - 1];
    if (prev && /(\b[A-Z]|\b(St|Mr|Mrs|Dr|Jr|Sr|c|ca))\.$/.test(prev)) out[out.length - 1] = `${prev} ${piece}`;
    else if (piece.trim()) out.push(piece.trim());
  }
  return out;
}

// Descriptions open with '"Title" is a compelling <genre> painting...', which
// repeats the search result's title. Sentences that say what the picture shows
// make a better snippet.
const OPENER = /^.{0,160}?\bis\s+(a|an)\s+/i;
const SUMMARY = /^(overall|in summary|ultimately|in conclusion)\b/i;
const SUBJECT = /\b(depict|portray|show|capture|feature|illustrat|represent|scene|story)/i;
// Guesses ("likely features", "may evoke") make a weak snippet.
const HEDGE = /\b(likely|may|might|possibly|perhaps|presumably)\b/i;

function cutAtWord(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[,;:\s]+$/, "")}…`;
}

export function artworkSeoDescription(a: SeoArtwork): string {
  const year = firstYear(a.date);
  const facts = [a.artist, year, a.style ? styleDisplayName(a.style) : ""].filter(Boolean).join(", ");
  const lead = facts ? `${facts}. ` : "";

  // A title with full stops in it ("Balaklava. Fishing nets.") must not be
  // split into sentences.
  const title = (a.title ?? "").trim();
  const MASK = "\u0000";
  const masked = title.includes(".") ? (t: string) => t.split(title).join(MASK) : (t: string) => t;
  const unmask = (t: string) => t.split(MASK).join(title);
  const all = a.description ? sentences(masked(plainText(a.description))).map(unmask) : [];
  const rest = all.length > 1 && OPENER.test(all[0]) ? all.slice(1) : all;
  const subject = (s: string) => SUBJECT.test(s) && !SUMMARY.test(s);
  const rank = (s: string) => (HEDGE.test(s) ? 2 : subject(s) ? 0 : 1);
  const ordered = rest.map((s, i) => ({ s, i })).sort((x, y) => rank(x.s) - rank(y.s) || x.i - y.i).map((x) => x.s);

  let body = "";
  for (const s of ordered) {
    if (body && (lead + body + " " + s).length > DESCRIPTION_MAX) break;
    body = body ? `${body} ${s}` : s;
  }
  if (!body) {
    const genre = a.genre ? ` ${a.genre.toLowerCase()}` : "";
    body = `See the${genre} work, its emotional profile and related artworks.`;
  }
  return cutAtWord(lead + body, DESCRIPTION_MAX);
}
